import { Title4 } from '@common/components/Typography/Typography';
import {
  ExperienceStepSchema,
  PackagesFormValues,
  PackageType,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { useGetSpaPackage } from '@features/spa';
import { useTranslations } from '@lang';
import { useQueryClient } from '@tanstack/react-query';
import { some } from '@utils/some';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { SpaTitleWrapper } from '../../../components/ExperiencesGroup/ExperiencesGroup.styles';
import { usePackageConfiguration } from '../../../contexts';
import { useExperienceSameTimes } from '../../../hooks/useExperienceSameTimes';
import { PageTitle } from '../../FormSteps.styles';
import { useGroupOptionsByDateAndOptionGroup } from '../../hooks/useGroupOptionsByDate';
import { useHashNavigation } from '../../hooks/useHashNavigation';
import { useStepFooter } from '../../hooks/useStepFooter';
import { SpaSectionWrapper } from '../ExperiencesStep.styles';
import {
  ExperienceTitleWrapper,
  ExperienceWrapper,
  SpaEntitySelectionWrapper,
  SpaEntityStepWrapper,
  TitleWrapper,
} from './MultiDayExperiencesStep.styles';
import { MultipleOptionsExperienceTimeslotSelection } from './MultipleOptionsExperienceTimeslotSelection/MultipleOptionsExperienceTimeslotSelection';
import { SingleOptionExperienceTimeslotSelection } from './SingleOptionExperienceTimeslotSelection/SingleOptionExperienceTimeslotSelection';

export const MultiDayExperiencesTimeslotsStep = () => {
  const {
    property,
    journeyParams,
    spaPackageId,
    stayDuration,
    totalGuestAmount,
    spaOpenTime,
    spaCloseTime,
  } = usePackageConfiguration();

  const numberOfGuests = totalGuestAmount;

  useHashNavigation({ hash: 'experiencesTimeslotsStep' });

  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const { watch, getValues } = useFormContext<PackagesFormValues>();
  const selectedExperiences = watch('selectedExperiences');
  const guests = getValues('guests') || [];

  const { experienceSameTimes, toggleExperienceSameTime, canEnableMultiGuestSameTime } =
    useExperienceSameTimes({
      selectedExperiences,
      numberOfGuests,
      defaultValue: true,
      queryClient,
      propertyId: Number(property.id),
      spaPackageId: Number(spaPackageId),
      spaOpenTime,
      spaCloseTime,
    });

  const { data: spaDay } = useGetSpaPackage(
    Number(property.id),
    Number(spaPackageId),
    property.defaultLanguage,
    true
  );

  const selectedExperiencesWithoutExtras = (selectedExperiences || []).filter(
    (exp) => exp.type !== 'spa-extras'
  );

  // Multi-day packages: PackageType.FLEXIBLE
  const selectedExperiencesByDay = useGroupOptionsByDateAndOptionGroup(
    moment(journeyParams.fromDate),
    selectedExperiencesWithoutExtras,
    PackageType.FLEXIBLE,
    stayDuration,
    numberOfGuests
  );
  selectedExperiencesByDay.forEach((date) => {
    date.options.sort((a, b) => {
      return a.isIncluded === b.isIncluded ? 0 : a.isIncluded ? 1 : -1;
    });
  });

  const allExperiences = selectedExperiencesByDay.flatMap(({ options }) => options);

  const totalTimeslotsSelected = guests.reduce((total, guest) => {
    return total + (guest.experiences?.filter((b) => !!b).length || 0);
  }, 0);

  const totalTimeSlotsRequired = allExperiences.length * numberOfGuests;

  const isValid =
    guests.every((guest) => {
      const experiences = guest.experiences || [];
      return ExperienceStepSchema.safeParse(experiences).success;
    }) && totalTimeslotsSelected === totalTimeSlotsRequired;

  const footer = useStepFooter({ isValid });

  return (
    <>
      <ExperienceWrapper>
        <ExperienceTitleWrapper>
          <SpaTitleWrapper>
            <PageTitle>Spa</PageTitle>
          </SpaTitleWrapper>
        </ExperienceTitleWrapper>
        <SpaSectionWrapper>
          <SpaEntityStepWrapper>
            <>
              {some(allExperiences) && (
                <>
                  {allExperiences.map((exp, expIndex) => {
                    const isSameTime = experienceSameTimes[expIndex] ?? false;
                    const dayNumberRange = exp.dayNumberRange
                      ? exp.dayNumberRange
                      : exp.dayNumber
                      ? [exp.dayNumber]
                      : [0];
                    if (exp.isIncluded) {
                      return exp.options.map((experience) => {
                        const currentIndex = expIndex;
                        const date = moment(journeyParams.fromDate)
                          .add(experience.dayNumber || 0, 'days')
                          .format('YYYY-MM-DD');
                        const optionId = Number(experience.optionId || experience.id);
                        const canToggle = canEnableMultiGuestSameTime(date, [optionId]);

                        return (
                          <SpaEntitySelectionWrapper
                            key={`experience-${experience.id}-${currentIndex}`}
                          >
                            {isSameTime ? (
                              <SingleOptionExperienceTimeslotSelection
                                experience={experience}
                                experienceIndex={currentIndex}
                                dayNumberRange={dayNumberRange}
                                configurationGroups={spaDay?.configurationGroups || []}
                                guestIndex={0}
                                isSameTimeForAllGuests={true}
                                onToggleSameTime={
                                  canToggle ? () => toggleExperienceSameTime(expIndex) : undefined
                                }
                                numberOfGuests={numberOfGuests}
                              />
                            ) : (
                              new Array(numberOfGuests)
                                .fill({})
                                .map((_, guestIndex) => (
                                  <SingleOptionExperienceTimeslotSelection
                                    experience={experience}
                                    experienceIndex={currentIndex}
                                    dayNumberRange={dayNumberRange}
                                    key={`guest-slot-${guestIndex}`}
                                    configurationGroups={spaDay?.configurationGroups || []}
                                    guestIndex={guestIndex}
                                    numberOfGuests={numberOfGuests}
                                    isSameTimeForAllGuests={false}
                                    onToggleSameTime={
                                      guestIndex === 0 && canToggle
                                        ? () => toggleExperienceSameTime(expIndex)
                                        : undefined
                                    }
                                  />
                                ))
                            )}
                          </SpaEntitySelectionWrapper>
                        );
                      });
                    } else if (some(exp.options)) {
                      return (
                        <SpaEntitySelectionWrapper
                          key={`experience-group-${exp.optionGroupId}-${expIndex}`}
                        >
                          <TitleWrapper>
                            <Title4>{t('spa.package.packageAvailabilityTitle')}</Title4>
                          </TitleWrapper>
                          {new Array(numberOfGuests).fill({}).map((_, guestIndex) => (
                            <MultipleOptionsExperienceTimeslotSelection
                              experiences={exp}
                              experienceIndex={expIndex}
                              dayNumberRange={dayNumberRange}
                              key={`guest-slot-${guestIndex}`}
                              configurationGroups={spaDay?.configurationGroups || []}
                              guestIndex={guestIndex}
                              numberOfGuests={numberOfGuests}
                              isSameTimeForAllGuests={false}
                            />
                          ))}
                        </SpaEntitySelectionWrapper>
                      );
                    }
                    return null;
                  })}
                </>
              )}
            </>
          </SpaEntityStepWrapper>
        </SpaSectionWrapper>
      </ExperienceWrapper>
      {footer}
    </>
  );
};
